import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { TroubleshootingFunnelApi } from '@openx/types/troubleshootingFunnel.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TroubleshootingParams } from '../../utils/hooks/useTroubleshootingFetch.ts';
import { FunnelEligibleBidRequests } from './components/FunnelEligibleBidRequests.tsx';
import { FunnelStepRecursive } from './components/FunnelStepRecursive.tsx';
import { MetricsOverview } from './components/MetricsOverview.tsx';
import type { DateRange } from './components/TroubleshootingDateFilter.tsx';
import { TroubleshootingNoDataMessage } from './components/TroubleshootingNoDataMessage.tsx';
import { type FunnelRenderState, funnelRenderState, hasFunnelData, type TimePeriod, timePeriod } from './constants.ts';

const styles: { [key: string]: SxProps<Theme> } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme => theme.spacing(2),
    height: '100%',
    maxWidth: '704px',
    minWidth: 0,
    padding: theme => `${theme.spacing(3.5)} ${theme.spacing(3)}`,
    width: '100%',
  },
  wrapper: {
    backgroundColor: theme => theme.palette.background.boxLight,
    height: '100%',
    width: '100%',
  },
};

interface DealTroubleshootingFunnelProps {
  dealId: string;
  selectedPeriod: TimePeriod;
  customDateRange?: DateRange;
  funnelData?: TroubleshootingFunnelApi;
  error?: string | null;
  troubleshootingParams?: TroubleshootingParams;
  onRefetch?: () => void | Promise<void>;
}

export const DealTroubleshootingFunnel = ({
  dealId,
  selectedPeriod,
  funnelData,
  error,
  onRefetch,
}: DealTroubleshootingFunnelProps) => {
  const { t } = useTranslation();

  const isLast24h = useMemo(() => selectedPeriod === timePeriod.Last24Hours, [selectedPeriod]);

  const renderState: FunnelRenderState = useMemo(() => {
    if (error) {
      return funnelRenderState.ERROR;
    }
    if (!hasFunnelData(funnelData)) {
      return funnelRenderState.NO_DATA;
    }
    return funnelRenderState.DATA_AVAILABLE;
  }, [funnelData, error]);

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.container} data-test="troubleshooting-funnel-form">
        {renderState === funnelRenderState.DATA_AVAILABLE && (
          <Box sx={{ padding: theme => theme.spacing(0, 0, 0) }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                marginBottom: 2,
              }}
            >
              {t('Key KPIs and a funnel view summarizing Deal performance for the selected date range.')}
            </Typography>
          </Box>
        )}

        {(renderState === funnelRenderState.ERROR || renderState === funnelRenderState.NO_DATA) && (
          <TroubleshootingNoDataMessage onReload={onRefetch} />
        )}

        {renderState === funnelRenderState.DATA_AVAILABLE && funnelData && (
          <>
            <MetricsOverview data={funnelData} dealId={dealId} isLast24h={isLast24h} />
            <Divider />
            <FunnelEligibleBidRequests data={funnelData} />
            <FunnelStepRecursive data={funnelData} isLast24h={isLast24h} />
          </>
        )}
      </Box>
    </Box>
  );
};
